import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Card,
  CardContent,
  Grid,
} from "@/components/ui";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import CheckIcon from "@mui/icons-material/Check";
import { generateTemplate, getDocumentDesigns } from "../../../services/companyAdminService";
import DocumentDesignThumbnail from "@/components/DocumentDesignThumbnail/DocumentDesignThumbnail";

const STEP_ACTIVE_COLOR = "#1976d2";
/** רקע אטום (לא שקוף) לעיגולי שלבים שעדיין לא פעילים */
const STEP_PENDING_BG = "#575558";

function StepIconComponent(props) {
  const { active, completed, icon } = props;
  const isActiveOrCompleted = active || completed;
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isActiveOrCompleted ? STEP_ACTIVE_COLOR : STEP_PENDING_BG,
        color: "#fff",
        fontWeight: 700,
        fontSize: "0.75rem",
      }}
    >
      {completed ? <CheckIcon sx={{ fontSize: 16 }} /> : icon}
    </Box>
  );
}

const LINE_HEIGHT = 2;
const CONNECTOR_LINE_COLOR = "rgba(255, 255, 255, 0.35)";
const ICON_SIZE = 28;
/** מרכז השלב הראשון/אחרון ביחס לרוחב (8 שלבים → מרכז שלב 0 ב-1/16, שלב 7 ב-15/16) */
const LINE_EDGE_PERCENT = (1 / 16) * 100; // 6.25%
const LINE_SPAN_PERCENT = 100 - LINE_EDGE_PERCENT * 2; // 87.5%

const loadingDotsSx = {
  display: "inline-flex",
  dir: "ltr",
  minWidth: "1.5em",
  justifyContent: "flex-start",
  fontSize: "1.25rem",
  lineHeight: 1,
  "& .loading-dot": {
    animation: "loadingDots 1.4s ease-in-out infinite both",
    fontSize: "inherit",
    "&:nth-of-type(1)": { animationDelay: "0s" },
    "&:nth-of-type(2)": { animationDelay: "0.2s" },
    "&:nth-of-type(3)": { animationDelay: "0.4s" },
  },
  "@keyframes loadingDots": {
    "0%, 80%, 100%": { opacity: 0.35 },
    "40%": { opacity: 1 },
  },
};

const DOCUMENT_TYPES = [
  { value: "הסכם שירות", labelEn: "Service agreement" },
  { value: "NDA / שמירת סודיות", labelEn: "NDA / Confidentiality" },
  { value: "טופס הסכמה", labelEn: "Consent form" },
  { value: "אישור תקנון / מדיניות", labelEn: "Policy acknowledgment" },
  { value: "הזמנה / הצעת מחיר", labelEn: "Order / Quote" },
  { value: "ויתור / שחרור", labelEn: "Waiver / Release" },
  { value: "פרוטוקול", labelEn: "Protocol / Minutes" },
  { value: "אחר", labelEn: "Other" },
];

const PLACEHOLDER_OPTIONS = [
  { id: "clientName", labelHe: "שם הלקוח", labelEn: "Client name" },
  { id: "clientPhone", labelHe: "טלפון", labelEn: "Phone" },
  { id: "clientEmail", labelHe: "אימייל", labelEn: "Email" },
  { id: "clientAddress", labelHe: "כתובת", labelEn: "Address" },
  { id: "currentDate", labelHe: "תאריך", labelEn: "Date" },
  { id: "companyName", labelHe: "שם החברה", labelEn: "Company name" },
  { id: "serviceName", labelHe: "שם השירות", labelEn: "Service name" },
  { id: "serviceDescription", labelHe: "תיאור השירות", labelEn: "Service description" },
  { id: "price", labelHe: "מחיר", labelEn: "Price" },
  { id: "currency", labelHe: "מטבע", labelEn: "Currency" },
  { id: "paymentTerms", labelHe: "תנאי תשלום", labelEn: "Payment terms" },
  { id: "duration", labelHe: "משך", labelEn: "Duration" },
  { id: "projectName", labelHe: "שם הפרויקט", labelEn: "Project name" },
  { id: "startDate", labelHe: "תאריך התחלה", labelEn: "Start date" },
  { id: "endDate", labelHe: "תאריך סיום", labelEn: "End date" },
];

const TONE_OPTIONS = [
  { value: "פורמלי", labelEn: "Formal" },
  { value: "עסקי", labelEn: "Business" },
  { value: "ידידותי", labelEn: "Friendly" },
];

const LENGTH_OPTIONS = [
  { value: "קצר", labelEn: "Short" },
  { value: "בינוני", labelEn: "Medium" },
  { value: "מפורט", labelEn: "Detailed" },
];

const STRUCTURE_OPTIONS = [
  {
    value: "טבלת פרטים בראש וסעיפים ממוספרים בגוף",
    labelHe: "טבלת פרטים בראש המסמך + סעיפים ממוספרים בגוף",
    labelEn: "Details table at top + numbered sections in body",
  },
  { value: "פסקאות בלבד", labelHe: "פסקאות בלבד", labelEn: "Paragraphs only" },
];

const STEP_LABELS_HE = [
  "עיצוב מסמך",
  "צבעים",
  "סוג מסמך ושפה",
  "צדדים",
  "משתנים (Placeholders)",
  "תוכן",
  "הגדרות טכניות",
  "סיכום",
];
const STEP_LABELS_EN = [
  "Document design",
  "Colors",
  "Document type & language",
  "Parties",
  "Variables (Placeholders)",
  "Content",
  "Technical",
  "Summary",
];

/** Preset color palettes for document designs */
const COLOR_PALETTES = [
  { id: "ocean", nameHe: "אוקיינוס", nameEn: "Ocean", primary: "#1565c0", secondary: "#0d47a1", accent: "#42a5f5", headerBg: "#1565c0", textOnHeader: "#ffffff" },
  { id: "forest", nameHe: "יער", nameEn: "Forest", primary: "#2e7d32", secondary: "#1b5e20", accent: "#66bb6a", headerBg: "#2e7d32", textOnHeader: "#ffffff" },
  { id: "sunset", nameHe: "שקיעה", nameEn: "Sunset", primary: "#c62828", secondary: "#b71c1c", accent: "#ef5350", headerBg: "#c62828", textOnHeader: "#ffffff" },
  { id: "corporate", nameHe: "קורפורטיבי", nameEn: "Corporate", primary: "#1a237e", secondary: "#0d47a1", accent: "#3949ab", headerBg: "#1a237e", textOnHeader: "#ffffff" },
  { id: "purple", nameHe: "סגול", nameEn: "Purple", primary: "#6a1b9a", secondary: "#4a148c", accent: "#9c27b0", headerBg: "#6a1b9a", textOnHeader: "#ffffff" },
  { id: "slate", nameHe: "אפור", nameEn: "Slate", primary: "#455a64", secondary: "#263238", accent: "#78909c", headerBg: "#455a64", textOnHeader: "#ffffff" },
  { id: "teal", nameHe: "טורקיז", nameEn: "Teal", primary: "#00695c", secondary: "#004d40", accent: "#26a69a", headerBg: "#00695c", textOnHeader: "#ffffff" },
  { id: "amber", nameHe: "ענבר", nameEn: "Amber", primary: "#e65100", secondary: "#bf360c", accent: "#ff9800", headerBg: "#e65100", textOnHeader: "#ffffff" },
  { id: "indigo", nameHe: "אינדיגו", nameEn: "Indigo", primary: "#303f9f", secondary: "#1a237e", accent: "#5c6bc0", headerBg: "#303f9f", textOnHeader: "#ffffff" },
  { id: "rose", nameHe: "ורוד", nameEn: "Rose", primary: "#c2185b", secondary: "#880e4f", accent: "#ec407a", headerBg: "#c2185b", textOnHeader: "#ffffff" },
  { id: "cyan", nameHe: "ציאן", nameEn: "Cyan", primary: "#0097a7", secondary: "#006064", accent: "#26c6da", headerBg: "#0097a7", textOnHeader: "#ffffff" },
  { id: "lime", nameHe: "ליים", nameEn: "Lime", primary: "#689f38", secondary: "#33691e", accent: "#9ccc65", headerBg: "#689f38", textOnHeader: "#ffffff" },
  { id: "deep-orange", nameHe: "כתום עמוק", nameEn: "Deep orange", primary: "#d84315", secondary: "#bf360c", accent: "#ff5722", headerBg: "#d84315", textOnHeader: "#ffffff" },
  { id: "blue-grey", nameHe: "כחול־אפור", nameEn: "Blue grey", primary: "#546e7a", secondary: "#37474f", accent: "#90a4ae", headerBg: "#546e7a", textOnHeader: "#ffffff" },
  { id: "deep-purple", nameHe: "סגול עמוק", nameEn: "Deep purple", primary: "#4527a0", secondary: "#311b92", accent: "#7e57c2", headerBg: "#4527a0", textOnHeader: "#ffffff" },
  { id: "emerald", nameHe: "אמרלד", nameEn: "Emerald", primary: "#00796b", secondary: "#004d40", accent: "#4db6ac", headerBg: "#00796b", textOnHeader: "#ffffff" },
  { id: "coral", nameHe: "אלמוגים", nameEn: "Coral", primary: "#e64a19", secondary: "#bf360c", accent: "#ff7043", headerBg: "#e64a19", textOnHeader: "#ffffff" },
  { id: "navy", nameHe: "צי", nameEn: "Navy", primary: "#0d47a1", secondary: "#01579b", accent: "#1976d2", headerBg: "#0d47a1", textOnHeader: "#ffffff" },
  { id: "mint", nameHe: "מנטה", nameEn: "Mint", primary: "#00897b", secondary: "#00695c", accent: "#4dd0e1", headerBg: "#00897b", textOnHeader: "#ffffff" },
  { id: "wine", nameHe: "יין", nameEn: "Wine", primary: "#6d1b1b", secondary: "#4e1515", accent: "#c62828", headerBg: "#6d1b1b", textOnHeader: "#ffffff" },
  { id: "gold", nameHe: "זהב", nameEn: "Gold", primary: "#f9a825", secondary: "#f57f17", accent: "#ffca28", headerBg: "#b8860b", textOnHeader: "#ffffff" },
  { id: "steel", nameHe: "פלדה", nameEn: "Steel", primary: "#37474f", secondary: "#263238", accent: "#607d8b", headerBg: "#37474f", textOnHeader: "#ffffff" },
  { id: "sage", nameHe: "מרווה", nameEn: "Sage", primary: "#558b2f", secondary: "#33691e", accent: "#7cb342", headerBg: "#558b2f", textOnHeader: "#ffffff" },
  { id: "crimson", nameHe: "ארגמן", nameEn: "Crimson", primary: "#ad1457", secondary: "#880e4f", accent: "#e91e63", headerBg: "#ad1457", textOnHeader: "#ffffff" },
  { id: "sky", nameHe: "שמיים", nameEn: "Sky", primary: "#0288d1", secondary: "#01579b", accent: "#03a9f4", headerBg: "#0288d1", textOnHeader: "#ffffff" },
  { id: "olive", nameHe: "זית", nameEn: "Olive", primary: "#827717", secondary: "#558b2f", accent: "#9e9d24", headerBg: "#558b2f", textOnHeader: "#ffffff" },
  { id: "brick", nameHe: "לבנים", nameEn: "Brick", primary: "#8d6e63", secondary: "#5d4037", accent: "#a1887f", headerBg: "#5d4037", textOnHeader: "#ffffff" },
  { id: "violet", nameHe: "סגול ויולט", nameEn: "Violet", primary: "#7b1fa2", secondary: "#4a148c", accent: "#ab47bc", headerBg: "#7b1fa2", textOnHeader: "#ffffff" },
];

const initialWizardState = {
  documentDesignId: "",
  documentDesignColors: null,
  documentType: "",
  language: "he",
  title: "",
  partiesDescription: "",
  requiredPlaceholders: ["clientName", "currentDate"],
  optionalPlaceholders: [],
  customPlaceholders: "",
  contentDescription: "",
  requiredClauses: "",
  forbiddenClauses: "",
  tone: "",
  length: "",
  direction: "rtl",
  structure: "",
  includeLogo: false,
};

export default function AiGenerateWizard({ open, onClose, onSuccess, isRTL }) {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(initialWizardState);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [designs, setDesigns] = useState([]);
  const stepLabels = isRTL ? STEP_LABELS_HE : STEP_LABELS_EN;
  const totalSteps = stepLabels.length;

  useEffect(() => {
    if (open) {
      getDocumentDesigns()
        .then((data) => setDesigns(data?.designs || []))
        .catch(() => setDesigns([]));
    }
  }, [open]);

  const handleClose = () => {
    setActiveStep(0);
    setForm(initialWizardState);
    setError(null);
    onClose();
  };

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleRequired = (id) => {
    setForm((f) =>
      f.requiredPlaceholders.includes(id)
        ? { ...f, requiredPlaceholders: f.requiredPlaceholders.filter((x) => x !== id) }
        : { ...f, requiredPlaceholders: [...f.requiredPlaceholders, id] }
    );
  };

  const toggleOptional = (id) => {
    setForm((f) =>
      f.optionalPlaceholders.includes(id)
        ? { ...f, optionalPlaceholders: f.optionalPlaceholders.filter((x) => x !== id) }
        : { ...f, optionalPlaceholders: [...f.optionalPlaceholders, id] }
    );
  };

  const handleGenerate = async () => {
    setError(null);
    setGenerating(true);
    try {
      const customList = form.customPlaceholders
        ? form.customPlaceholders.split(/[\s,]+/).filter(Boolean)
        : [];
      const payload = {
        documentType: form.documentType || (isRTL ? "מסמך לחתימה" : "Signable document"),
        language: form.language,
        direction: form.direction,
        title: form.title || undefined,
        partiesDescription: form.partiesDescription || undefined,
        requiredPlaceholders: form.requiredPlaceholders,
        optionalPlaceholders: form.optionalPlaceholders,
        customPlaceholders: customList,
        contentDescription: form.contentDescription || undefined,
        requiredClauses: form.requiredClauses
          ? form.requiredClauses.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
          : [],
        forbiddenClauses: form.forbiddenClauses
          ? form.forbiddenClauses.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
          : [],
        tone: form.tone || undefined,
        length: form.length || undefined,
        structure: form.structure || undefined,
        includeLogo: form.includeLogo,
        documentDesignId: form.documentDesignId || undefined,
        documentDesignColors: form.documentDesignColors || undefined,
      };
      const data = await generateTemplate(payload);
      if (data?.templateHtml) {
        onSuccess({
          templateHtml: data.templateHtml,
          suggestedName: data.suggestedName,
          suggestedDescription: data.suggestedDescription,
          placeholders: data.placeholders || [],
          documentDesignId: form.documentDesignId || null,
          documentDesignColors: form.documentDesignColors || null,
          contentHtml: data.contentHtml ?? null,
        });
        handleClose();
      } else {
        setError(data?.errors?.join(" ") || (isRTL ? "לא הוחזר תוכן" : "No content returned"));
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || (isRTL ? "שגיאה ביצירה" : "Generation failed"));
    } finally {
      setGenerating(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: {
        return (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {isRTL ? "בחר עיצוב למסמך (אופציונלי)" : "Choose a document design (optional)"}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)", lg: "repeat(5, 1fr)" },
                gap: 1.5,
              }}
            >
              <Card
                  variant="outlined"
                  sx={{
                    cursor: "pointer",
                    border: form.documentDesignId === "" ? "2px solid" : "1px solid",
                    borderColor: form.documentDesignId === "" ? "primary.main" : "divider",
                    bgcolor: form.documentDesignId === "" ? "action.selected" : "background.paper",
                    minHeight: 220,
                  }}
                  onClick={() => { update("documentDesignId", ""); update("documentDesignColors", null); }}
                >
                  <CardContent sx={{ py: 2, "&:last-child": { pb: 2 }, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{ width: 128, height: 80, minWidth: 128, minHeight: 80, mx: "auto", mb: 1.5, borderRadius: 1, bgcolor: "grey.100", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography variant="caption" color="text.secondary">
                        {isRTL ? "ללא עיצוב" : "No design"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} textAlign="center">
                      {isRTL ? "ללא עיצוב" : "No design"}
                    </Typography>
                  </CardContent>
                </Card>
              {designs.map((d) => (
                <Card
                  key={d.id}
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      border: form.documentDesignId === d.id ? "2px solid" : "1px solid",
                      borderColor: form.documentDesignId === d.id ? "primary.main" : "divider",
                      bgcolor: form.documentDesignId === d.id ? "action.selected" : "background.paper",
                      minHeight: 220,
                    }}
                    onClick={() => {
                      update("documentDesignId", d.id);
                      const pal = COLOR_PALETTES[0];
                      update("documentDesignColors", pal ? { primary: pal.primary, secondary: pal.secondary, accent: pal.accent, headerBg: pal.headerBg, textOnHeader: pal.textOnHeader } : null);
                    }}
                  >
                    <CardContent sx={{ py: 2, "&:last-child": { pb: 2 }, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <Box sx={{ width: 128, height: 80, minWidth: 128, minHeight: 80, mb: 1.5, flexShrink: 0 }}>
                        <DocumentDesignThumbnail designId={d.id} />
                      </Box>
                      <Typography variant="body2" fontWeight={600} textAlign="center">
                        {isRTL ? d.nameHe : d.nameEn}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                        {d.category}
                      </Typography>
                    </CardContent>
                  </Card>
              ))}
            </Box>
          </Stack>
        );
      }
      case 1: {
        const hasDesign = !!form.documentDesignId;
        return (
          <Stack spacing={2}>
            {hasDesign ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? "בחר פלטת צבעים" : "Choose a color palette"}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)", md: "repeat(5, 1fr)", lg: "repeat(7, 1fr)" },
                    gap: 1.5,
                  }}
                >
                  {COLOR_PALETTES.map((pal) => {
                    const isSelected = form.documentDesignColors?.primary === pal.primary;
                    return (
                      <Card
                        key={pal.id}
                        variant="outlined"
                        sx={{
                          cursor: "pointer",
                          border: isSelected ? "2px solid" : "1px solid",
                          borderColor: isSelected ? "primary.main" : "divider",
                          bgcolor: isSelected ? "action.selected" : "background.paper",
                        }}
                        onClick={() => update("documentDesignColors", { primary: pal.primary, secondary: pal.secondary, accent: pal.accent, headerBg: pal.headerBg, textOnHeader: pal.textOnHeader })}
                      >
                        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                          <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: pal.primary }} />
                            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: pal.secondary }} />
                            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: pal.accent }} />
                          </Box>
                          <Typography variant="caption" fontWeight={600} display="block">
                            {isRTL ? pal.nameHe : pal.nameEn}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {isRTL ? "לא נבחר עיצוב – דלג לשלב הבא" : "No design selected – proceed to next step"}
              </Typography>
            )}
          </Stack>
        );
      }
      case 2:
        return (
          <Stack spacing={2.5}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{isRTL ? "סוג מסמך" : "Document type"}</InputLabel>
              <Select
                value={form.documentType}
                label={isRTL ? "סוג מסמך" : "Document type"}
                onChange={(e) => update("documentType", e.target.value)}
              >
                <MenuItem value="">
                  <em>{isRTL ? "בחר..." : "Select..."}</em>
                </MenuItem>
                {DOCUMENT_TYPES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {isRTL ? o.value : o.labelEn}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{isRTL ? "שפה" : "Language"}</InputLabel>
              <Select
                value={form.language}
                label={isRTL ? "שפה" : "Language"}
                onChange={(e) => update("language", e.target.value)}
              >
                <MenuItem value="he">{isRTL ? "עברית" : "Hebrew"}</MenuItem>
                <MenuItem value="en">{isRTL ? "אנגלית" : "English"}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              variant="outlined"
              label={isRTL ? "כותרת המסמך (אופציונלי)" : "Document title (optional)"}
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              size="small"
              fullWidth
              placeholder={isRTL ? "לדוגמה: הסכם שירות" : "e.g. Service Agreement"}
            />
          </Stack>
        );
      case 3:
        return (
          <Stack spacing={2.5}>
            <TextField
              variant="outlined"
              label={isRTL ? "תיאור הצדדים" : "Parties description"}
              value={form.partiesDescription}
              onChange={(e) => update("partiesDescription", e.target.value)}
              size="small"
              fullWidth
              placeholder={
                isRTL
                  ? "לדוגמה: מספק שירות ולקוח, או מעסיק ועובד"
                  : "e.g. Service provider and client, or employer and employee"
              }
            />
          </Stack>
        );
      case 4:
        return (
          <Stack spacing={2.5} sx={{ mt: 0 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {isRTL ? "בחר משתנים חובה (יופיעו במסמך)" : "Required variables (will appear in document)"}
            </Typography>
            <FormGroup row sx={{ flexWrap: "wrap", gap: 0.5 }}>
              {PLACEHOLDER_OPTIONS.map((o) => (
                <FormControlLabel
                  key={o.id}
                  control={
                    <Checkbox
                      checked={form.requiredPlaceholders.includes(o.id)}
                      onChange={() => toggleRequired(o.id)}
                      size="small"
                    />
                  }
                  label={isRTL ? o.labelHe : o.labelEn}
                  sx={{ mr: 1 }}
                />
              ))}
            </FormGroup>
            <Typography variant="subtitle2" color="text.secondary">
              {isRTL ? "משתנים אופציונליים" : "Optional variables"}
            </Typography>
            <FormGroup row sx={{ flexWrap: "wrap", gap: 0.5 }}>
              {PLACEHOLDER_OPTIONS.filter((p) => !form.requiredPlaceholders.includes(p.id)).map((o) => (
                <FormControlLabel
                  key={o.id}
                  control={
                    <Checkbox
                      checked={form.optionalPlaceholders.includes(o.id)}
                      onChange={() => toggleOptional(o.id)}
                      size="small"
                    />
                  }
                  label={isRTL ? o.labelHe : o.labelEn}
                  sx={{ mr: 1 }}
                />
              ))}
            </FormGroup>
            <TextField
              variant="outlined"
              label={isRTL ? "משתנים נוספים (מופרדים בפסיק)" : "Custom variables (comma-separated)"}
              value={form.customPlaceholders}
              onChange={(e) => update("customPlaceholders", e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g. projectName, warrantyMonths"
            />
          </Stack>
        );
      case 5:
        return (
          <Stack spacing={2.5}>
            <TextField
              variant="outlined"
              label={isRTL ? "תיאור התוכן" : "Content description"}
              value={form.contentDescription}
              onChange={(e) => update("contentDescription", e.target.value)}
              size="small"
              fullWidth
              placeholder={
                isRTL
                  ? "תאר במשפט או שניים מה המסמך צריך לכלול..."
                  : "Describe in a sentence or two what the document should include..."
              }
            />
            <TextField
              variant="outlined"
              label={isRTL ? "סעיפים שחייבים להופיע (מופרדים בפסיק)" : "Required sections (comma-separated)"}
              value={form.requiredClauses}
              onChange={(e) => update("requiredClauses", e.target.value)}
              size="small"
              fullWidth
              placeholder={
                isRTL
                  ? "אחריות הצדדים, תשלום, סודיות"
                  : "Parties' responsibilities, payment, confidentiality"
              }
            />
            <Box>
              <TextField
                variant="outlined"
                label={isRTL ? "סעיפים שלא לכלול במסמך" : "Sections to exclude from document"}
                value={form.forbiddenClauses}
                onChange={(e) => update("forbiddenClauses", e.target.value)}
                size="small"
                fullWidth
                placeholder={
                  isRTL
                    ? "בוררות, הגבלת אחריות, ויתור על תביעה"
                    : "Arbitration, liability cap, waiver of claim"
                }
              />
              <FormHelperText sx={{ mt: 0.5, mx: 0 }}>
                {isRTL
                  ? "סעיפים שלא תרצה שיופיעו במסמך. למשל: בוררות, הגבלת אחריות, ויתור על תביעה."
                  : "Sections you do not want in the document. E.g.: arbitration, liability cap, waiver of claim."}
              </FormHelperText>
            </Box>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{isRTL ? "טון" : "Tone"}</InputLabel>
              <Select
                value={form.tone}
                label={isRTL ? "טון" : "Tone"}
                onChange={(e) => update("tone", e.target.value)}
              >
                <MenuItem value="">
                  <em>{isRTL ? "בחר..." : "Select..."}</em>
                </MenuItem>
                {TONE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {isRTL ? o.value : o.labelEn}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{isRTL ? "אורך" : "Length"}</InputLabel>
              <Select
                value={form.length}
                label={isRTL ? "אורך" : "Length"}
                onChange={(e) => update("length", e.target.value)}
              >
                <MenuItem value="">
                  <em>{isRTL ? "בחר..." : "Select..."}</em>
                </MenuItem>
                {LENGTH_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {isRTL ? o.value : o.labelEn}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        );
      case 6:
        return (
          <Stack spacing={2.5}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{isRTL ? "כיוון" : "Direction"}</InputLabel>
              <Select
                value={form.direction}
                label={isRTL ? "כיוון" : "Direction"}
                onChange={(e) => update("direction", e.target.value)}
              >
                <MenuItem value="rtl">RTL (עברית)</MenuItem>
                <MenuItem value="ltr">LTR (English)</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{isRTL ? "מבנה" : "Structure"}</InputLabel>
              <Select
                value={form.structure}
                label={isRTL ? "מבנה" : "Structure"}
                onChange={(e) => update("structure", e.target.value)}
              >
                <MenuItem value="">
                  <em>{isRTL ? "בחר..." : "Select..."}</em>
                </MenuItem>
                {STRUCTURE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {isRTL ? (o.labelHe ?? o.value) : o.labelEn}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.includeLogo}
                  onChange={(e) => update("includeLogo", e.target.checked)}
                  size="small"
                />
              }
              label={isRTL ? "כלול מקום ללוגו בראש המסמך" : "Include logo placeholder at top"}
            />
          </Stack>
        );
      case 7:
        return (
          <Stack spacing={1}>
            <Typography variant="body2">
              <strong>{isRTL ? "סוג:" : "Type:"}</strong> {form.documentType || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>{isRTL ? "שפה:" : "Language:"}</strong> {form.language === "he" ? (isRTL ? "עברית" : "Hebrew") : "English"}
            </Typography>
            <Typography variant="body2">
              <strong>{isRTL ? "כיוון:" : "Direction:"}</strong> {form.direction.toUpperCase()}
            </Typography>
            <Typography variant="body2">
              <strong>{isRTL ? "משתנים חובה:" : "Required vars:"}</strong> {form.requiredPlaceholders.join(", ")}
            </Typography>
            {form.contentDescription && (
              <Typography variant="body2">
                <strong>{isRTL ? "תיאור:" : "Description:"}</strong> {form.contentDescription.slice(0, 100)}…
              </Typography>
            )}
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ textAlign: "center" }}>
        {isRTL ? "יצירת מסמך עם AI" : "Generate document with AI"}
      </DialogTitle>
      <DialogContent dividers sx={{ direction: isRTL ? "rtl" : "ltr" }}>
        <Box sx={{ pt: 0, pb: 2, position: "relative", direction: isRTL ? "rtl" : "ltr" }}>
          {/* קו אפור – מחבר ממרכז השלב הראשון למרכז השלב האחרון */}
          <Box
            sx={{
              position: "absolute",
              top: ICON_SIZE / 2 - LINE_HEIGHT / 2,
              left: `${LINE_EDGE_PERCENT}%`,
              right: `${LINE_EDGE_PERCENT}%`,
              height: LINE_HEIGHT,
              background: CONNECTOR_LINE_COLOR,
              borderRadius: 1,
            }}
          />
          {/* קו כחול – מהשלב הראשון עד השלב הפעיל */}
          <Box
            sx={{
              position: "absolute",
              top: ICON_SIZE / 2 - LINE_HEIGHT / 2,
              height: LINE_HEIGHT,
              borderRadius: 1,
              background: STEP_ACTIVE_COLOR,
              ...(isRTL
                ? { right: `${LINE_EDGE_PERCENT}%`, left: "auto", width: `${(activeStep / (stepLabels.length - 1)) * LINE_SPAN_PERCENT}%` }
                : { left: `${LINE_EDGE_PERCENT}%`, right: "auto", width: `${(activeStep / (stepLabels.length - 1)) * LINE_SPAN_PERCENT}%` }),
            }}
          />
          {/* שלבים – direction: rtl מציב את השלב הראשון מימין */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              position: "relative",
              zIndex: 1,
              direction: isRTL ? "rtl" : "ltr",
            }}
          >
            {stepLabels.map((label, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <StepIconComponent
                  active={activeStep === i}
                  completed={activeStep > i}
                  icon={i + 1}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    marginTop: 1,
                    textAlign: "center",
                    maxWidth: "100%",
                    color:
                      activeStep === i
                        ? "primary.main"
                        : activeStep > i
                          ? "text.primary"
                          : "text.secondary",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            minHeight: 220,
            pt: 0.5,
            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
              height: 44,
              minHeight: 44,
              boxSizing: "border-box",
            },
            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall:has(.MuiInputBase-inputMultiline)": {
              height: "auto",
              minHeight: "unset",
            },
          }}
        >
          {renderStepContent()}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", flexDirection: "row", direction: "ltr" }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          {activeStep < totalSteps - 1 ? (
            <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)}>
              {isRTL ? "הבא" : "Next"}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generating}
              sx={generating && isRTL ? { direction: "rtl" } : undefined}
            >
              {generating ? (
                <>
                  {!isRTL && (
                    <Box
                      component="span"
                      sx={loadingDotsSx}
                    >
                      <span className="loading-dot">.</span>
                      <span className="loading-dot">.</span>
                      <span className="loading-dot">.</span>
                    </Box>
                  )}
                  {isRTL ? "יוצר" : "Generating"}
                  {isRTL && (
                    <Box
                      component="span"
                      sx={loadingDotsSx}
                    >
                      <span className="loading-dot">.</span>
                      <span className="loading-dot">.</span>
                      <span className="loading-dot">.</span>
                    </Box>
                  )}
                </>
              ) : isRTL ? "צור מסמך" : "Generate"}
            </Button>
          )}
          {activeStep > 0 && activeStep < totalSteps - 1 && (
            <Button onClick={() => setActiveStep((s) => s - 1)} disabled={generating}>
              {isRTL ? "הקודם" : "Back"}
            </Button>
          )}
        </Box>
        <Button onClick={handleClose} disabled={generating}>
          {isRTL ? "ביטול" : "Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
